import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { Component, inject, input, model, OnInit } from '@angular/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { filter } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ChipsService } from 'src/app/core/services/chips.service'
import { EventService } from 'src/app/core/services/event.service'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { IJsonLogic } from 'src/app/core/interfaces/json-logic.interface'
import { FilterId } from 'src/app/core/interfaces/filter.interface'
import { jsonLogic } from 'src/app/core/utils/json-logic.util'
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox'
import { TrackingService, UserEvent } from 'src/app/core/services/tracking.service'
import { IListFilterItem } from 'src/app/core/interfaces/list-filter.interface'
import { LogService } from 'src/app/core/services/log.service'
import { FilterService } from 'src/app/core/services/filter.service'


type IListFilterState = IListFilterItem[]

export function getEmptyListFilterState(): IListFilterState {
  return []
}

export const checkListFilterEmpty = (state: IListFilterState) => state.length === 0

interface IListFilterChipData {
  item: IListFilterItem
}

@Component({
  selector: 'app-list-filter',
  templateUrl: './list-filter.component.html',
  styleUrls: ['./list-filter.component.scss'],
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
  ],
})
export class ListFilterComponent implements OnInit {
  private _logger = inject(LogService)
  private _chipsService = inject(ChipsService)
  private _eventService = inject(EventService)
  private _trackingService = inject(TrackingService)
  private _filterService = inject(FilterService)

  filterId = input.required<FilterId>()
  state = model.required<IListFilterState>()
  items = input.required<IListFilterItem[]>()
  delimiter = input<string>('|')

  constructor() {
    this._eventService.chipRemoved$.pipe(
      takeUntilDestroyed(),
      filter(chip => chip.filterId === this.filterId()),
    ).subscribe((chip) => {
      const data = chip.data as IListFilterChipData
      this._logger.debug('Chip removed', chip)
      if (!data) {
        this._logger.error('Chip data missing', chip)
      }
      const {
        item,
      } = data
      this.remove(item)
    })

    this._eventService.allChipsRemoved$.pipe(
      takeUntilDestroyed(),
    ).subscribe(() => this.clearAll())
  }

  ngOnInit(): void {
    this.initChips()
  }

  initChips(): void {
    this.state().forEach(item => {
      this._chipsService.addChip({
        id: itemToId(item, this.filterId(), this.delimiter()),
        label: item.label ?? this._filterService.getCustomFilterLabel(this.filterId()),
        filterId: this.filterId(),
        data: {
          item,
        },
      })
    })
  }

  handleChange(event: MatCheckboxChange, item: IListFilterItem): void {
    if (event.checked) {
      this.add(item)
    } else {
      this.remove(item)
    }
  }

  add(item: IListFilterItem): void {
    const newItem = this.buildStateData(item)
    if (!this.state().find(i => i.value === item.value && i.field === item.field)) {
      this.state.update(items => [...items, newItem])
      this._chipsService.addChip({
        id: itemToId(item, this.filterId(), this.delimiter()),
        label: item.label ?? this._filterService.getCustomFilterLabel(this.filterId()),
        filterId: this.filterId(),
        data: {
          item: newItem,
        }
      })
      this._trackingService.trackEvent(UserEvent.QuickFilterOptionAdded, newItem)
    }
  }

  remove(item: IListFilterItem): void {
    this.state.update(items => items.filter(i => i.value !== item.value || i.field !== item.field))
    this._chipsService.removeChip(itemToId(item, this.filterId(), this.delimiter()))
  }

  clearAll(): void {
    this.state().forEach(item => {
      this._chipsService.removeChip(itemToId(item, this.filterId(), this.delimiter()))
    })
    this.state.set(getEmptyListFilterState())
  }

  isSelected(item: IListFilterItem): boolean {
    return !!this.state().find(i => i.field === item.field)
  }

  buildStateData(item: IListFilterItem): IListFilterItem {
    return  {
      type: item.type,
      value: item.value,
      field: item.field,
      label: item.label,
      display: item.display,
    }
  }

  static buildFilter(
    state: IListFilterState,
  ): IJsonLogic {
    const { and, equals, variable } = jsonLogic
    if (state.length === 0) {
      return {}
    }
    const items = state.map(item => {
      return equals(variable(item.field), item.value)
    })
    return and(...items)
  }
}

const itemToId = (item: IListFilterItem, filterId: string, delimiter: string) => [item.field, filterId].join(delimiter)
